import {
  DeleteOutlined,
  DownloadOutlined,
  PaperClipOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, Flex, notification, Tooltip, Typography, Upload } from "antd";
import { RcFile } from "antd/es/upload";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { MAX_UPLOAD_SIZE } from "../../modules/obligee/exciseLicences/exciseLicences.constants";
import { ApiResponseObject, DataPair, DocumentApi } from "../../types";
import { arrayBufferToBase64, downloadFile } from "../../utils/fileDownload";
import style from "./FileUploader.module.scss";
interface FileUploaderProps {
  onRemove: (arg?: any) => void;
  onUpload: (document: DocumentApi, uid?: string, fileSize?: number) => void;
  onDownload?: (documentId: number) => Promise<
    ApiResponseObject<{
      data: string;
      format: string;
    }>
  >;
  isDisabled?: boolean;
  initialDocument?: DataPair | null;
  uploadButtonLabel?: string;
  disableIfUploaded?: boolean;
  maxFileSize?: number;
  showAvailableUploadSize?: boolean;
}

const FileUploader = ({
  onRemove,
  onUpload,
  onDownload,
  initialDocument = null,
  isDisabled,
  uploadButtonLabel,
  disableIfUploaded,
  maxFileSize = MAX_UPLOAD_SIZE,
  showAvailableUploadSize,
}: FileUploaderProps) => {
  const [uploadedDocument, setUploadedDocument] = useState<
    (DataPair & { uid?: string; fileSize?: number }) | null
  >(null);
  const uploadedDocumentRef = useRef<HTMLElement>(null);

  const showDiv = () => {
    uploadedDocumentRef.current?.classList.remove(style.hideAnimation);
    uploadedDocumentRef.current?.classList.add(style.showAnimation);
  };

  const hideDiv = () => {
    uploadedDocumentRef.current?.classList.remove(style.showAnimation);
    uploadedDocumentRef.current?.classList.add(style.hideAnimation);
  };

  const handleUploadDocument = (event: any) => {
    if (event.file instanceof File) {
      try {
        showDiv();

        const reader = new FileReader();
        reader.readAsArrayBuffer(event.file);
        const type = event.file.type;

        const formatType = () => {
          const parts = event.file.name.split(".");
          if (parts.length > 1) {
            return parts[parts.length - 1];
          } else {
            return type;
          }
        };

        setUploadedDocument({
          naziv: event.file.name,
          id: null,
          uid: event.file.uid,
          fileSize: event.file.size,
        });
        const uid = event.file?.uid;
        const fileSize = event.file?.size;

        reader.onloadend = (event) => {
          if (event.target?.result) {
            const data = event.target?.result;

            onUpload?.(
              {
                data: arrayBufferToBase64(data as ArrayBuffer),
                format: formatType(),
              },
              uid,
              fileSize
            );
          }
        };
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleDownloadDocument = async () => {
    if (!uploadedDocument?.id || !onDownload) {
      return;
    }

    const response = await onDownload?.(uploadedDocument.id);

    if (!response) {
      return;
    }

    downloadFile(
      response.data?.data,
      uploadedDocument.naziv || "dokument_specifikacija",
      response.data?.format as any
    );
  };

  const handleRemoveDocument = () => {
    hideDiv();

    setTimeout(() => {
      onRemove?.(uploadedDocument);
      setUploadedDocument(null);
    }, 100);
  };

  function bytesToMB(bytes: number): string {
    const mb = bytes / (1024 * 1024);
    return mb % 1 === 0 ? mb.toFixed(0) : mb.toFixed(2);
  }

  function beforeUpload(file: RcFile) {
    const isPDF = file.type === "application/pdf" || file.name.endsWith(".pdf");
    if (!isPDF) {
      notification.error({ message: t("COMMON.FILE_TYPE_PDF") });
      return true;
    }

    if (file.size > maxFileSize) {
      if (showAvailableUploadSize) {
        notification.error({
          message: `${t(
            "COMMON.DOKUMENT_PREMASUJE_PREOSTALI_PROSTOR_OD"
          )} ${bytesToMB(maxFileSize)} MB`,
        });
      } else {
        notification.error({
          message: `${t("COMMON.DOKUMENT_JE_VECI_OD")} ${bytesToMB(
            maxFileSize
          )} MB`,
        });
      }

      return true;
    }

    return false;
  }

  useEffect(() => {
    setUploadedDocument(initialDocument);
  }, [initialDocument]);

  useEffect(() => {
    showDiv();
  }, []);

  return (
    <Flex flex={1} gap={8} vertical>
      {!isDisabled ? (
        <Upload
          maxCount={1}
          accept=".pdf"
          beforeUpload={beforeUpload}
          onChange={handleUploadDocument}
          showUploadList={false}
          customRequest={() => {}}
        >
          <Button
            type="primary"
            className={style.uploadButton}
            icon={<UploadOutlined />}
            disabled={isDisabled || (disableIfUploaded && !!uploadedDocument)}
          >
            {!uploadButtonLabel
              ? !uploadedDocument && !isDisabled
                ? t("AKCIZNE_MARKICE.IZABERI_DOKUMENT")
                : t("AKCIZNE_MARKICE.ZAMENI_DOKUMENT")
              : uploadButtonLabel}
          </Button>
        </Upload>
      ) : (
        <></>
      )}

      {uploadedDocument ? (
        <Flex
          align="center"
          gap={8}
          className={`${style.documentItem} ${style.showAnimation}`}
          ref={uploadedDocumentRef}
        >
          <Flex align="center" gap={2}>
            <PaperClipOutlined className={style.paperClipIcon} />

            <Typography.Paragraph
              className={style.documentName}
              ellipsis={{
                rows: 1,
                expandable: false,
                tooltip: uploadedDocument?.naziv,
              }}
            >
              {uploadedDocument?.naziv}
            </Typography.Paragraph>
          </Flex>
          {uploadedDocument?.id && onDownload ? (
            <Tooltip title={t("AKCIZNE_MARKICE.PREUZMI")}>
              <DownloadOutlined
                style={{ cursor: "pointer" }}
                onClick={handleDownloadDocument}
                className={style.downloadDocumentIcon}
              />
            </Tooltip>
          ) : (
            <></>
          )}
          {!isDisabled ? (
            <Tooltip title={t("AKCIZNE_MARKICE.UKLONI")}>
              <DeleteOutlined
                className={style.deleteDocumentIcon}
                onClick={handleRemoveDocument}
              />
            </Tooltip>
          ) : (
            <></>
          )}
        </Flex>
      ) : (
        <></>
      )}
    </Flex>
  );
};

export default FileUploader;
